'use client'

import * as TabsPrimitive from '@radix-ui/react-tabs'
import * as React from 'react'

import { cn } from '@/lib/utils'
import { cva } from 'class-variance-authority'

const tabsTriggerVariants = cva(
  'inline-flex items-center justify-center whitespace-nowrap px-8 py-1.5 text-sm font-bold text-neutral-800 ring-offset-background transition-all focus-visible:outline-none focus-visible:ring-2 font-criteria focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 gap-1 leading-5.5',
  {
    variants: {
      variant: {
        default:
          'border-b-2 border-b-transparent data-[state=active]:border-b-primary-500 data-[state=active]:text-primary-500 hover:border-b-primary-500 hover:text-primary-500',
        neutral:
          'border-b-2 border-b-transparent data-[state=active]:border-b-neutral-900 data-[state=active]:text-neutral-900 hover:border-b-neutral-900 hover:text-neutral-900',
        outline:
          'bg-transparent text-foreground border-neutral-900 mr-3 last-of-type:mr-0 border rounded-full data-[state=active]:text-light data-[state=active]:bg-neutral-900',
        filter:
          'data-[state=active]:bg-primary-500 data-[state=active]:text-primary-foreground bg-neutral-200 text-neutral-900 rounded-full mr-3 last-of-type:mr-0',
        shadcn:
          'data-[state=active]:bg-background data-[state=active]:text-foreground data-[state=active]:shadow-sm rounded-sm',
      },
      size: {
        default: 'h-10.5',
        small: 'h-6.5  py-1 px-3 text-xs font-normal',
      },
    },
    defaultVariants: {
      variant: 'default',
    },
  }
)

type TabsProps = {
  options: {
    value: string
    label: React.ReactNode
    content?: React.ReactNode
    className?: string
  }[]
  defaultValue?: string
  variant?: 'default' | 'outline' | 'shadcn' | 'filter' | 'neutral'
  size?: 'default' | 'small'
  className?: string
  wrapperClassName?: string
  triggerClassName?: string
  onChange?: (value: string) => void
}

export const Tabs = ({
  options,
  defaultValue,
  variant,
  size,
  className,
  triggerClassName,
  wrapperClassName,
  onChange,
}: TabsProps) => {
  const listRef = React.useRef<HTMLDivElement>(null)
  const tabRefs = React.useRef<Record<string, HTMLButtonElement | null>>({})

  const handleClick = (value: string) => {
    const tab = tabRefs.current[value]
    const list = listRef.current
    onChange?.(value)
    if (tab && list) {
      const tabCenter = tab.offsetLeft - list.offsetLeft + tab.offsetWidth / 2
      const scrollPosition = tabCenter - list.offsetWidth / 2

      list.scrollTo({
        left: scrollPosition,
        behavior: 'smooth',
      })
    }
  }

  return (
    <TabsPrimitive.Root
      defaultValue={defaultValue || options[0]?.value}
      className={wrapperClassName}
    >
      <TabsPrimitive.List
        ref={listRef}
        className={cn(
          'no-scrollbar flex h-10 w-auto min-w-full items-center justify-start overflow-x-auto overflow-y-hidden rounded-md bg-transparent p-1 text-sm',
          className
        )}
      >
        {options.map(({ value, label }) => (
          <TabsPrimitive.Trigger
            key={value}
            value={value}
            ref={(el) => {
              tabRefs.current[value] = el
            }}
            className={cn(
              'inline-block min-w-max truncate',
              tabsTriggerVariants({ variant, size }),
              triggerClassName
            )}
            onClick={() => handleClick(value)}
          >
            {label}
          </TabsPrimitive.Trigger>
        ))}
      </TabsPrimitive.List>
      {options.map(({ value, content, className }) =>
        !content ? null : (
          <TabsPrimitive.Content
            key={value}
            value={value}
            className={cn('mt-4', className)}
          >
            {content}
          </TabsPrimitive.Content>
        )
      )}
    </TabsPrimitive.Root>
  )
}
